/**
 * Tap to target (order 714, after the weapon view; docs/WEAPONS.md "Tap to target"): on a phone, a
 * finger tapped on a person or a car in the street while armed makes it the lock — the red brackets
 * jump onto them and HIT / FIRE goes their way without having to face them first. A tap on empty street
 * lets a chosen lock go. On a desktop the right button (aim) pressed over somebody does the same.
 *
 * Only taps that land on the 3D view count: the stick zone, the knobs, the minimap and every paper on
 * the HUD sit above the canvas and keep their fingers. A tap is short and still (`isTap`), so a drag
 * or a hold is never a target pick. Presentation-side input: the pick goes to the weapons service
 * (`chooseLock`), which checks reach and sight; online the shot it turns still goes to the server.
 */
import * as THREE from 'three';
import type { GameContext } from '../core/context';
import type { System } from '../core/system';
import { touchUi } from '../input/touch';
import { haptic } from '../ui/mobile';
import { isFlick, isTap, pickSwipeTarget, pickTapTarget, type TapCandidate } from './tapTarget';

/** Candidates are gathered this far around the player (m); the weapons service checks the real reach. */
const PICK_RADIUS = 45;
/** Heights (m) the bodies are projected at: a person's middle, a car's roofline. */
const PERSON_Y = 0.9;
const CAR_Y = 0.8;

interface PendingTap {
  x: number;
  y: number;
  /** A finger on empty street clears a chosen lock; a right click there leaves things be. */
  clearOnEmpty: boolean;
  /** A flick (swipe to switch): its direction in px; the lock moves to the next candidate that way. */
  flick?: { dx: number; dy: number };
}

export function createTapTargetSystem(): System {
  let ctx: GameContext;
  let canvas: HTMLCanvasElement | null = null;
  const downs = new Map<number, { x: number; y: number; at: number }>();
  let pending: PendingTap | null = null;
  const v = new THREE.Vector3();
  const cands: TapCandidate[] = [];
  let last: { kind: string; id: number; ok: boolean } | null = null;
  const offs: (() => void)[] = [];

  function armed(): boolean {
    const w = ctx.services.tryGet('weapons');
    const ui = ctx.services.tryGet('ui');
    return !!w && w.active && w.def.cls !== 'unarmed' && !!ui?.free;
  }

  function onDown(e: PointerEvent): void {
    if (e.target !== canvas) return;
    if (e.pointerType === 'mouse') {
      // Desktop: the aim button pressed over somebody locks onto them.
      if (e.button === 2 && armed()) pending = { x: e.clientX, y: e.clientY, clearOnEmpty: false };
      return;
    }
    if (!touchUi.enabled) return;
    // No emulated mouse after a finger on the street: the browser's mousedown would be a left click (FIRE).
    e.preventDefault();
    downs.set(e.pointerId, { x: e.clientX, y: e.clientY, at: performance.now() });
  }

  function onUp(e: PointerEvent): void {
    const d = downs.get(e.pointerId);
    if (!d) return;
    downs.delete(e.pointerId);
    const ms = performance.now() - d.at;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (!armed()) return;
    if (isTap(ms, dx, dy)) pending = { x: d.x, y: d.y, clearOnEmpty: true };
    else if (isFlick(ms, dx, dy)) pending = { x: d.x, y: d.y, clearOnEmpty: false, flick: { dx, dy } };
  }

  function onCancel(e: PointerEvent): void {
    downs.delete(e.pointerId);
  }

  /** Project the lock candidates to canvas px (with their body radius on screen). */
  function project(): void {
    cands.length = 0;
    const w = ctx.services.tryGet('weapons');
    const rig = ctx.services.tryGet('cameraRig');
    if (!w || !ctx.render || !canvas) return;
    const cam = ctx.camera;
    cam.updateMatrixWorld();
    const W = canvas.clientWidth;
    const H = canvas.clientHeight;
    const right = rig ? rig.screenRight() : { x: 1, z: 0 };
    for (const t of w.lockCandidates(PICK_RADIUS)) {
      if (t.down) continue;
      const y = t.kind === 'vehicle' ? CAR_Y : PERSON_Y;
      v.set(t.x, y, t.z).project(cam);
      if (v.z > 1 || v.z < -1) continue;
      const sx = ((v.x + 1) / 2) * W;
      const sy = ((1 - v.y) / 2) * H;
      // Body radius on screen: a metre to the screen's right, projected, scaled by the radius.
      v.set(t.x + right.x, y, t.z + right.z).project(cam);
      const ppm = Math.hypot(((v.x + 1) / 2) * W - sx, ((1 - v.y) / 2) * H - sy);
      cands.push({ kind: t.kind, id: t.id, sx, sy, rPx: t.r * ppm });
    }
  }

  /** The player's own spot on the canvas (px), for a flick with no lock to start from. */
  function playerOnScreen(): { sx: number; sy: number } | null {
    const p = ctx.services.tryGet('player');
    if (!p || !canvas) return null;
    v.set(p.pos.x, PERSON_Y, p.pos.z).project(ctx.camera);
    return { sx: ((v.x + 1) / 2) * canvas.clientWidth, sy: ((1 - v.y) / 2) * canvas.clientHeight };
  }

  function resolve(tap: PendingTap): void {
    const w = ctx.services.tryGet('weapons');
    if (!w || !canvas || !armed()) return;
    const r = canvas.getBoundingClientRect();
    project();
    let hit: TapCandidate | null;
    if (tap.flick) {
      // Swipe to switch: from the current lock's spot on the screen (the player's own with no lock) to the
      // nearest candidate the flick points at. Nobody that way: the lock stays as it is.
      const l = w.lock;
      const from = l ? cands.find((c) => c.kind === l.kind && c.id === l.id) : undefined;
      const origin = from ?? playerOnScreen();
      hit = origin ? pickSwipeTarget(origin.sx, origin.sy, tap.flick.dx, tap.flick.dy, cands, l ? { kind: l.kind, id: l.id } : null) : null;
      if (!hit) return;
    } else hit = pickTapTarget(tap.x - r.left, tap.y - r.top, cands);
    if (!hit) {
      if (tap.clearOnEmpty && w.lock && w.info().lock?.chosen) w.chooseLock(null);
      last = null;
      return;
    }
    const ok = w.chooseLock({ kind: hit.kind, id: hit.id });
    last = { kind: hit.kind, id: hit.id, ok };
    if (ok && touchUi.enabled && ctx.services.tryGet('save')?.settings.touchHaptics !== false) haptic(8);
  }

  return {
    id: 'tap-target',
    order: 714,
    init(c) {
      ctx = c;
      c.hooks.register('tapTargetState', () => ({ last, candidates: cands.map((k) => ({ kind: k.kind, id: k.id, x: Math.round(k.sx), y: Math.round(k.sy), r: Math.round(k.rPx) })) }));
      c.hooks.register('tapTargetAt', (kind: string, id: number) => {
        // Tests: where on the page a candidate stands right now (client px), to tap it for real.
        project();
        const k = cands.find((q) => q.kind === kind && q.id === id);
        const r = canvas?.getBoundingClientRect();
        return k && r ? { x: Math.round(k.sx + r.left), y: Math.round(k.sy + r.top) } : null;
      });
      if (!c.render || typeof window === 'undefined') return;
      canvas = c.render.canvas;
      canvas.addEventListener('pointerdown', onDown);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onCancel);
      offs.push(() => {
        canvas?.removeEventListener('pointerdown', onDown);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onCancel);
      });
    },
    update() {
      if (!pending) return;
      const tap = pending;
      pending = null;
      resolve(tap);
    },
    dispose() {
      for (const off of offs) off();
      offs.length = 0;
      downs.clear();
    },
  };
}

declare module '../core/hooks' {
  interface BhHooks {
    /** Tap to target read-out: the last pick and the candidates as last projected (canvas px). */
    tapTargetState(): Record<string, unknown>;
    /** Page position (client px) of a lock candidate, or null when it isn't one / not on screen. */
    tapTargetAt(kind: string, id: number): { x: number; y: number } | null;
  }
}
